import React, { useContext, useEffect, useState } from 'react'
import { apiGet, apiDelete } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import OButton from 'components/reusable/OButton'
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
import dayjs from 'dayjs'
import ListDocument from './ListDocument'
import AddDocument from './AddDocument'
import EditDocument from './EditDocument'
import ListCategory from './ListCategory' // New component
import AddCategory from './AddCategory' // New component
import EditCategory from './EditCategory' // New component
import Pagination from '../Pagination'
import AuthContext from 'context/AuthContext'
import { useTranslation } from 'react-i18next'
import helpers from '../../utils/helpers'
import OSearch from 'components/reusable/OSearch'
import OSelect from 'components/reusable/OSelect'
import useToastContext from "hooks/useToastContext";

const DocumentManager = () => {
  const { user, updatePageName } = useContext(AuthContext)
  const manager =
    user?.permission?.find(e => e.manager === 'document_manager') ?? {}
  const { t } = useTranslation()
  const notification = useToastContext();

  // Tab state
  const [activeTab, setActiveTab] = useState('documents') // 'documents' or 'categories'

  // Document states
  const [documentList, setDocumentList] = useState([])
  const [documentAdd, setDocumentAdd] = useState(false)
  const [documentEdit, setDocumentEdit] = useState(false)
  const [currentDocumentItem, setCurrentDocumentItem] = useState('')
  const [documentView, setDocumentView] = useState(false)
  
  // Category states
  const [categoryList, setCategoryList] = useState([])
  const [categoryAdd, setCategoryAdd] = useState(false)
  const [categoryEdit, setCategoryEdit] = useState(false)
  const [currentCategoryItem, setCurrentCategoryItem] = useState('')

  // Common states
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10
  })
  
  // Filter states
  const [documentFilterData, setDocumentFilterData] = useState({
    startDate: '',
    endDate: '',
    status: '',
    category: '',
    searchKey: '',
    isFilter: false,
    isReset: false
  })

  const [categoryFilterData, setCategoryFilterData] = useState({
    startDate: '',
    endDate: '',
    status: '',
    searchKey: '',
    isFilter: false,
    isReset: false
  })

  const [sort, setSort] = useState({
    sortBy: 'createdAt',
    sortType: 'desc'
  })

  const statusOptions = [
    { value: '', label: t('ALL_STATUS') },
    { value: 'active', label: t('ACTIVE') },
    { value: 'inactive', label: t('INACTIVE') }
  ]

  const [categoryOptions, setCategoryOptions] = useState([
    { value: '', label: t('ALL_CATEGORIES') }
  ])

  // Fetch categories for dropdown
  const fetchCategoriesForDropdown = async () => {
    try {
      const payload = {
        page: 1,
        pageSize: 100,
        status: 'active'
      }
      const path = apiPath.categoryList
      const result = await apiGet(path, payload)
      const categories = result?.data?.results?.docs || []
      
      const options = [
        { value: '', label: t('ALL_CATEGORIES') },
        ...categories.map(cat => ({
          value: cat._id,
          label: cat.name
        }))
      ]
      setCategoryOptions(options)
    } catch (error) {
      console.log('error fetching categories:', error.message)
    }
  }

  // Document handlers
  const handleEditDocument = item => {
    setCurrentDocumentItem(item)
    setDocumentEdit(!documentEdit)
  }

  const handleViewDocument = item => {
    setCurrentDocumentItem(item)
    setDocumentView(!documentView)
  }

  const handleAddDocument = () => {
    setDocumentAdd(!documentAdd)
  }

  // Category handlers
  const handleEditCategory = item => {
    setCurrentCategoryItem(item)
    setCategoryEdit(!categoryEdit)
  }

  const handleAddCategory = () => {
    setCategoryAdd(!categoryAdd)
  }

  const handlePageClick = event => {
    const newPage = event.selected + 1
    setPage(newPage)
  }

  // Fetch documents
  const fetchDocuments = async (data = {}) => {
    try {
      const { status, startDate, endDate, searchKey, isFilter, category } = documentFilterData
      if (
        (data?.deletePage && documentList?.length >= 1) ||
        (isFilter && status && data?.statusChange && documentList?.length >= 1)
      ) {
        setPage(page - 1)
        setPaginationObj({ ...paginationObj, page: page - 1 })
      }

      const payload = {
        page,
        pageSize,
        status: status,
        category: category,
        startDate: helpers?.ternaryCondition(
          startDate,
          dayjs(startDate).format('YYYY-MM-DD'),
          null
        ),
        endDate: helpers?.ternaryCondition(
          endDate,
          dayjs(endDate).format('YYYY-MM-DD'),
          null
        ),
        keyword: searchKey,
        sortBy: sort.sortBy,
        sortType: sort.sortType
      }
      const path = apiPath.documentList
      const result = await apiGet(path, payload)
      const response = result?.data?.results
      const resultStatus = result?.data?.success
      setDocumentList(response?.docs)
      setPaginationObj({
        ...paginationObj,
        page: helpers?.ternaryCondition(resultStatus, response.page, null),
        pageCount: helpers?.ternaryCondition(
          resultStatus,
          response.totalPages,
          null
        ),
        perPageItem: helpers?.ternaryCondition(
          resultStatus,
          response?.docs.length,
          null
        ),
        totalItems: helpers?.ternaryCondition(
          resultStatus,
          response.totalDocs,
          null
        )
      })
    } catch (error) {
      console.log('error in get all document list==>>>>', error.message)
    }
  }

  // Fetch categories
  const fetchCategories = async (data = {}) => {
    try {
      const { status, startDate, endDate, searchKey, isFilter } = categoryFilterData
      if (
        (data?.deletePage && categoryList?.length >= 1) ||
        (isFilter && status && data?.statusChange && categoryList?.length >= 1)
      ) {
        setPage(page - 1)
        setPaginationObj({ ...paginationObj, page: page - 1 })
      }

      const payload = {
        page,
        pageSize,
        status: status,
        startDate: helpers?.ternaryCondition(
          startDate,
          dayjs(startDate).format('YYYY-MM-DD'),
          null
        ),
        endDate: helpers?.ternaryCondition(
          endDate,
          dayjs(endDate).format('YYYY-MM-DD'),
          null
        ),
        keyword: searchKey,
        sortBy: sort.sortBy,
        sortType: sort.sortType
      }
      const path = apiPath.categoryList
      const result = await apiGet(path, payload)
      const response = result?.data?.results
      const resultStatus = result?.data?.success
      setCategoryList(response?.docs)
      setPaginationObj({
        ...paginationObj,
        page: helpers?.ternaryCondition(resultStatus, response.page, null),
        pageCount: helpers?.ternaryCondition(
          resultStatus,
          response.totalPages,
          null
        ),
        perPageItem: helpers?.ternaryCondition(
          resultStatus,
          response?.docs.length,
          null
        ),
        totalItems: helpers?.ternaryCondition(
          resultStatus,
          response.totalDocs,
          null
        )
      })
    } catch (error) {
      console.log('error in get all category list==>>>>', error.message)
    }
  }

  const deleteDocument = async (id) => {
    try {
      const confirmDelete = window.confirm(t('CONFIRM_DELETE_DOCUMENT'))
      if (!confirmDelete) return

      const path = `${apiPath.documentDelete}/${id}`
      const result = await apiDelete(path)
      if (result?.data?.success) {
        // helpers.showToast(result.data.message, 'success')
         notification.success(result?.data?.message);
        fetchDocuments({ deletePage: true })
      } else {
        // helpers.showToast(result?.data?.message, 'error')
        notification.success(result?.data?.message);
      }
    } catch (error) {
      console.log('error deleting document:', error.message)
      // helpers.showToast(error.message, 'error')
      notification.success(error.message);
    }
  }

  const deleteCategory = async (id) => {
    try {
      const confirmDelete = window.confirm(t('CONFIRM_DELETE_CATEGORY'))
      if (!confirmDelete) return

      const path = `${apiPath.categoryDelete}/${id}`
      const result = await apiDelete(path)
      if (result?.data?.success) {
        notification.success(result?.data?.message);
        // helpers.showToast(result.data.message, 'success')
        fetchCategories({ deletePage: true })
        fetchCategoriesForDropdown() // Refresh dropdown
      } else {
        notification.success(result?.data?.message);
        // helpers.showToast(result?.data?.message, 'error')
      }
    } catch (error) {
      console.log('error deleting category:', error.message)
      notification.success(error.message);
      // helpers.showToast(error.message, 'error')
    }
  }

  useEffect(() => {
    if (activeTab === 'documents') {
      fetchDocuments()
    } else {
      fetchCategories()
    }
  }, [page, activeTab === 'documents' ? documentFilterData : categoryFilterData, sort])

  useEffect(() => {
    fetchCategoriesForDropdown()
  }, [])

  const handleReset = () => {
    if (activeTab === 'documents') {
      setDocumentFilterData({
        startDate: '',
        endDate: '',
        status: '',
        category: '',
        searchKey: '',
        isFilter: false,
        isReset: true
      })
    } else {
      setCategoryFilterData({
        startDate: '',
        endDate: '',
        status: '',
        searchKey: '',
        isFilter: false,
        isReset: true
      })
    }
    setPage(1)
    setSearchTerm('')
  }

  const handleDateChange = (start, end) => {
    setPage(1)
    if (activeTab === 'documents') {
      setDocumentFilterData({
        ...documentFilterData,
        startDate: start,
        endDate: end,
        isFilter: true
      })
    } else {
      setCategoryFilterData({
        ...categoryFilterData,
        startDate: start,
        endDate: end,
        isFilter: true
      })
    }
  }

  const handleStatusFilter = (value) => {
    setPage(1)
    if (activeTab === 'documents') {
      setDocumentFilterData({
        ...documentFilterData,
        status: value,
        isFilter: true
      })
    } else {
      setCategoryFilterData({
        ...categoryFilterData,
        status: value,
        isFilter: true
      })
    }
  }

  const handleCategoryFilter = (value) => {
    setPage(1)
    setDocumentFilterData({
      ...documentFilterData,
      category: value,
      isFilter: true
    })
  }

  useEffect(() => {
    updatePageName(t('DOCUMENT_MANAGER'))
  }, [])

  useEffect(() => {
    helpers.setDebouncingFunction({
      isInitialized,
      setIsInitialized,
      debouncedSearchTerm,
      searchTerm,
      filterData: activeTab === 'documents' ? documentFilterData : categoryFilterData,
      setFilterData: activeTab === 'documents' ? setDocumentFilterData : setCategoryFilterData,
      setPage
    })
  }, [debouncedSearchTerm, activeTab])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [searchTerm])

  const currentFilterData = activeTab === 'documents' ? documentFilterData : categoryFilterData

  return (
    <>
      <div className='bg-[#F9F9F9]'>
        <div className='px-3 py-4'>
          <div className='bg-white border border-[#E9EDF9] rounded-lg'>
            {/* Tabs */}
            <div className='border-b border-[#E3E3E3]'>
              <div className='flex'>
                <button
                  className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors duration-200 ${
                    activeTab === 'documents'
                      ? 'border-LightBlue text-LightBlue'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => {
                    setActiveTab('documents')
                    setPage(1)
                  }}
                >
                  {t('DOCUMENTS')}
                </button>
                <button
                  className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors duration-200 ${
                    activeTab === 'categories'
                      ? 'border-LightBlue text-LightBlue'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => {
                    setActiveTab('categories')
                    setPage(1)
                  }}
                >
                  {t('DOCUMENT_CATEGORIES')}
                </button>
              </div>
            </div>

            {/* Filter Form */}
            <form className='border-b border-b-[#E3E3E3] 2xl:flex gap-2 px-4 py-3'>
              <div className='col-span-2 flex flex-wrap items-center mb-2 2xl:mb-0'>
                <div className='flex items-center lg:pt-0 pt-3 flex-wrap'>
                  <div className='relative flex items-center mb-3'>
                    <OSearch
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      placeholder={
                        activeTab === 'documents' 
                          ? t('SEARCH_BY_DOCUMENT_NAME')
                          : t('SEARCH_BY_CATEGORY_NAME')
                      }
                    />
                  </div>
                  
                  <div className='ml-3 mb-3'>
                    <OSelect
                      options={statusOptions}
                      value={currentFilterData.status}
                      onChange={handleStatusFilter}
                      placeholder={t('SELECT_STATUS')}
                      className='min-w-[150px]'
                    />
                  </div>

                  {activeTab === 'documents' && (
                    <div className='ml-3 mb-3'>
                      <OSelect
                        options={categoryOptions}
                        value={currentFilterData.category}
                        onChange={handleCategoryFilter}
                        placeholder={t('SELECT_CATEGORY')}
                        className='min-w-[200px]'
                      />
                    </div>
                  )}

                  <ODateRangePicker
                    handleDateChange={handleDateChange}
                    isReset={currentFilterData?.isReset}
                    setIsReset={activeTab === 'documents' ? setDocumentFilterData : setCategoryFilterData}
                  />

                  <OButton
                    label='Reset'
                    type='button'
                    onClick={handleReset}
                    extraClasses='ml-3 mb-3'
                    style={{ borderRadius: '0.5rem' }}
                  />
                </div>
              </div>
              <div className='flex items-center justify-end px-4 ms-auto mb-3'>
                <button
                  title={activeTab === 'documents' ? t('ADD_DOCUMENT') : t('ADD_CATEGORY')}
                  type='button'
                  className='bg-LightBlue flex text-sm px-8 ml-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue whitespace-nowrap'
                  onClick={() => activeTab === 'documents' ? handleAddDocument() : handleAddCategory()}
                >
                  + {activeTab === 'documents' ? t('ADD_DOCUMENT') : t('ADD_CATEGORY')}
                </button>
              </div>
            </form>

            {/* Document List */}
            {activeTab === 'documents' && (
              <>
                <ListDocument
                  documentList={documentList}
                  fetchDocuments={fetchDocuments}
                  page={page}
                  handleEdit={handleEditDocument}
                  handleView={handleViewDocument}
                  deleteDocument={deleteDocument}
                  currentItem={currentDocumentItem}
                  documentView={documentView}
                  setSort={setSort}
                  sort={sort}
                  manager={manager}
                />

                {documentEdit && (
                  <EditDocument
                    handleEdit={handleEditDocument}
                    currentItem={currentDocumentItem}
                    fetchDocuments={fetchDocuments}
                    user={user}
                    fetchCategoriesForDropdown={fetchCategoriesForDropdown}
                  />
                )}

                {documentAdd && (
                  <AddDocument
                    fetchDocuments={fetchDocuments}
                    documentAdd={documentAdd}
                    handleCategory={handleAddDocument}
                    setPage={setPage}
                    user={user}
                    fetchCategoriesForDropdown={fetchCategoriesForDropdown}
                  />
                )}
              </>
            )}

            {/* Category List */}
            {activeTab === 'categories' && (
              <>
                <ListCategory
                  categoryList={categoryList}
                  fetchCategories={fetchCategories}
                  page={page}
                  handleEdit={handleEditCategory}
                  deleteCategory={deleteCategory}
                  currentItem={currentCategoryItem}
                  setSort={setSort}
                  sort={sort}
                  manager={manager}
                />

                {categoryEdit && (
                  <EditCategory
                    handleEdit={handleEditCategory}
                    currentItem={currentCategoryItem}
                    fetchCategories={fetchCategories}
                    user={user}
                  />
                )}

                {categoryAdd && (
                  <AddCategory
                    fetchCategories={fetchCategories}
                    categoryAdd={categoryAdd}
                    handleCategory={handleAddCategory}
                    setPage={setPage}
                    user={user}
                    fetchCategoriesForDropdown={fetchCategoriesForDropdown}
                  />
                )}
              </>
            )}

            {paginationObj?.totalItems ? (
              <Pagination
                handlePageClick={handlePageClick}
                options={paginationObj}
                page={page}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}

export default DocumentManager